import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Heading, Text } from "@nbs/ui";
import { STORY_WOOD_INDICATOR_ID, STORY_WOOD_USAGE_QUERY } from "../api/documents";
import { STUDY_WHY } from "../data/landscapes";
import { STORY_IMAGES, STORY_PROFILE_IMAGES, StoryMedia } from "./StoryMedia";

/**
 * Published household-survey figures (rapport d'évaluation écosystémique).
 * The sample size and the wood-use share come from the catalogue when the
 * backend answers; the gender split is not exposed as an indicator yet.
 */
const SURVEY_RESPONDENTS = 502;
const SURVEY_WOMEN_SHARE = 71;
const SURVEY_WOOD_SHARE = 82;

export function StoryWhyStep() {
  const { data } = useQuery(STORY_WOOD_USAGE_QUERY, {
    variables: {
      indicateurId: STORY_WOOD_INDICATOR_ID,
      dateFrom: "2005-01-01",
      dateTo: "2025-12-31",
    },
    fetchPolicy: "cache-and-network",
  });

  /**
   * Household survey on forest wood: the sample it came from and the share of
   * respondents behind the dominant answer ("plus de 75 %" of families).
   */
  const wood = useMemo(() => {
    const distribution = data?.indicatorDistribution;
    const buckets: readonly { modalityLabel: string; value: number }[] =
      distribution?.buckets ?? [];
    if (buckets.length === 0) return null;
    const top = buckets.reduce((best, b) => (b.value > best.value ? b : best), buckets[0]!);
    return {
      share: top.value,
      sampleSize: distribution?.sampleSize ?? null,
    };
  }, [data]);

  return (
    <section
      className="nbs-story-step"
      data-testid="story-why-step"
      aria-labelledby="story-why-heading"
    >
      <div className="nbs-story-step__grid">
        <div className="nbs-story-step__copy">
          <p className="nbs-story-step__eyebrow">Étape 1 · Introduction</p>
          <Heading level={1} id="story-why-heading">
            {STUDY_WHY.title}
          </Heading>
          <p className="nbs-story-step__question">{STUDY_WHY.question}</p>
          <Text className="nbs-story-step__lead">{STUDY_WHY.lead}</Text>

          <Text className="nbs-story-step__lead" data-testid="story-why-wood-stat">
            L’enquête a recueilli les réponses de {wood?.sampleSize ?? SURVEY_RESPONDENTS}{" "}
            personnes, dont {SURVEY_WOMEN_SHARE} % de femmes. Parmi les personnes interrogées,{" "}
            {wood?.share ?? SURVEY_WOOD_SHARE} % estiment que plus de trois familles sur quatre
            utilisent le bois de la forêt.
          </Text>

          <Text className="nbs-story-step__lead" data-testid="story-why-partnership">
            Le projet est mis en œuvre par l’
            <a
              className="nbs-story-step__link"
              href="https://wusc.ca/fr/"
              target="_blank"
              rel="noopener noreferrer"
            >
              EUMC
            </a>
            , le{" "}
            <a
              className="nbs-story-step__link"
              href="https://www.ceci.ca/fr/"
              target="_blank"
              rel="noopener noreferrer"
            >
              CECI
            </a>
            , et leurs partenaires. Il est réalisé avec le soutien financier d’Affaires mondiales
            Canada.
          </Text>
        </div>

        <div className="nbs-story-step__visual">
          <StoryMedia
            src={STORY_IMAGES.vegetation}
            alt="Récolte de maïs dans un champ, photo CECI"
            caption="Communautés et production agricole — paysages d’étude"
            ratio="card"
          />
        </div>
      </div>

      <div className="nbs-story-profiles nbs-story-profiles--wide" data-testid="story-why-profiles">
        <header className="nbs-story-profiles__head">
          <Heading level={2}>Deux paysages, des enjeux distincts</Heading>
          <Text tone="muted" className="nbs-story-nesting__hint">
            Espèces, spécificité, rôle pour les communautés et menaces.
          </Text>
        </header>
        <div className="nbs-story-profiles__grid">
          {STUDY_WHY.profiles.map((profile) => {
            const image =
              STORY_PROFILE_IMAGES[profile.id as keyof typeof STORY_PROFILE_IMAGES] ??
              STORY_IMAGES.forest;
            return (
              <article key={profile.id} className="nbs-story-profile">
                <div className="nbs-story-profile__cover" aria-hidden="true">
                  <img src={image} alt="" loading="lazy" />
                </div>
                <header className="nbs-story-profile__header">
                  <Heading level={3}>{profile.title}</Heading>
                  <p className="nbs-story-profile__location">{profile.location}</p>
                </header>

                <dl className="nbs-story-profile__facts">
                  <div>
                    <dt>Spécificité</dt>
                    <dd>{profile.specificity}</dd>
                  </div>
                  <div>
                    <dt>Espèces</dt>
                    <dd>
                      <ul className="nbs-story-profile__list">
                        {profile.species.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                  <div>
                    <dt>Rôle pour les communautés</dt>
                    <dd>{profile.communityRole}</dd>
                  </div>
                  <div>
                    <dt>Menaces</dt>
                    <dd>
                      <ul className="nbs-story-profile__list">
                        {profile.threats.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                </dl>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
